import React, { useEffect, useState, FC, useCallback, useMemo } from "react";
import withStyles from '@mui/styles/withStyles';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Divider,
    FormControlLabel
  } from "@mui/material";
import Checkbox, { CheckboxProps } from '@mui/material/Checkbox';
import { green } from "@mui/material/colors";
import { FullWidthButton } from "src/framework/components/buttons/FullWidthButton";
import { IDataRequest, IDataResponse, STATUS_CODE, apiNoLoading } from "src/framework/providers/api";
import { notifierStore } from "src/framework/store/NotifierStore";
import { ConfirmarVisitaProperties, RequestConfirma, Termo, Termos } from "./types";

export const BASE_TEXT = "Ao confirmar a visita técnica, o cliente receberá um e-mail com os termos abaixo. Para prosseguir, aceite todos os termos:";

export interface TextoTermoVisitaTecnica {
    id: number
    checklist: Checklist[] | string[]
  }
  
  export interface Checklist {
    itens?: Item[]
    texto: string
  }
  
  export interface Item {
    texto: string
  }
  
const loadText = async ()=>{
    const request: IDataRequest = {
        url: `cadastros/visita_tecnica/textos?limit=1&orderBy=created_at-desc`,
    }
    const dados: TextoTermoVisitaTecnica = await apiNoLoading.get(request).then((response: IDataResponse) => {
        if (response.statusCode !== STATUS_CODE.OK) return null
        return (response.data?.rows?.[0] || null)
    });

    if (!dados) {
        notifierStore.setNotifier({
            title: "Erro!",
            content: "Não foi possível carregar os termos da visita técnica.",
            timeOut: 4000,
            onClose: () => {
                notifierStore.setOpen(false)
            }
          })
        notifierStore.setOpen(true);
        return null;
    }

    
    const termos: Termo[] = dados.checklist.map((item): Termo => {
        const listaDeItens = typeof item === 'string'
        
        return {
            accepted: false,
            text: listaDeItens ? item : item.texto,
            items: listaDeItens ? [] : item.itens ? item.itens.map((i) => ({ text: i.texto })) : [],
        }
    })

    return {
        id: dados.id,
        terms: termos,
    }
}

  
const ConfirmarVisita: FC<ConfirmarVisitaProperties> = ({
    open = true,
    idprojeto,
    onClose,
    onSuccess,
}) => {
    const [checkState, setCheckState] = useState<Termos>();
    const [loading, setLoading] = useState(false)

    const handleAccept = useCallback(async() => {
        setLoading(true)
        let content = ""
        const dataRequest: RequestConfirma = {
            projetos: [idprojeto],
            visita_tecnica_texto_id: checkState?.id,
        }
        const request: IDataRequest = {
            url: "/projetos/visita_tecnica/confirmar",
            data: dataRequest
        }

        const response: IDataResponse = await apiNoLoading.post(request);

        if (response.statusCode === STATUS_CODE.OK){
            content = "Confirmação de visita técnica realizada com sucesso!"
    
        } else {
            content = response.message
        }

        setLoading(false)

        notifierStore.setNotifier({
            title: "Aviso!",
            content: content,
            timeOut: 0,
            onClose: () => {
                notifierStore.setOpen(false)
                onSuccess();
                onClose();
            }
          })
          notifierStore.setOpen(true);
  
    }, [checkState?.id, idprojeto, onClose, onSuccess])
    
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, termId: number, termIndex: number) => {
        const updatedTerms = [...(checkState?.terms || [])];
        updatedTerms[termIndex] = {
            ...updatedTerms[termIndex],
            accepted: event.target.checked,
        };
        setCheckState({ ...(checkState || {}), id: checkState?.id || 0, terms: updatedTerms });
    };
    const GreenCheckbox = withStyles({
        root: {
            color: green[500],
            '&$checked': {
                color: green[600],
            },
        },
        checked: {},
        })((props: CheckboxProps) => <Checkbox color="default" {...props} />);

    const fullyChecked = useMemo(()=>{
        return checkState?.terms?.every(term => term.accepted) || false;
    },[checkState?.terms])

    useEffect(()=>{
        loadText().then(texts => {
            if (!texts) return;
            setCheckState(texts)
        });
    },[open])

    return (<>
        {checkState &&(
        <Box
            style={{width: "1400px"}}
        >
            <Dialog 
                open={open}
                maxWidth={'md'}
            >
                <DialogTitle id="dialog-confirmar-visita">
                    Confirmar visita técnica
                    <Divider />
                    <p>{BASE_TEXT}</p>
                </DialogTitle>
                <DialogContent>
                    <Box >
                        {checkState?.terms.map((d, i: number) => {
                            return (
                                <div style={{ marginBottom: "20px" }}>
                                    <FormControlLabel
                                        style={{ alignItems: "start" }}
                                        control={(
                                            <GreenCheckbox
                                                style={{ marginTop: "-10px"}}
                                                checked={d.accepted}
                                                onChange={(event) => handleChange(event, checkState.id, i)} name={`check${String(i)}`} id={String(i)}
                                            />
                                        )}
                                        label={d.text}
                                    />

                                {!!d.items?.length && (
                                    <ul style={{ marginTop: 0, marginLeft: "18px" }}>
                                        {d.items.map(item => (
                                            <li style={{ marginBottom: "10px" }}>{item.text}</li>
                                        ))}
                                    </ul>
                                )}
                                </div>);
                        })}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <FullWidthButton 
                        disabled={loading}
                        fullWidth={false}
                        color="secondary"
                        variant="outlined"
                        disableElevation
                        text="Discordo"
                        onClick={() => {
                            onClose();
                        }}
                    />
                    <FullWidthButton
                        disabled={!fullyChecked || loading}
                        fullWidth={false}
                        color="primary"
                        variant="contained"
                        disableElevation
                        text={loading ? "Salvando..." : "Aceito"}
                        onClick={() => {
                            if (loading) return;
                            handleAccept()
                        }}
                    />
                </DialogActions>
            </Dialog>
        </Box>
    )}
    </>
    )
};
export { ConfirmarVisita };